import { useEffect, useState } from 'react';

import { Sheet, SheetContent, SheetHeader, SheetTitle } from '@/components/ui/sheet';

import { useFilePreviewUrlQuery } from '@/query/knowledge.query';

import { getFileExtension, getFileName } from '@/lib/utils';

import { DocumentMetadataFooter } from './document-metadata-footer';
import { DocumentPreviewContent } from './document-preview-content';

type DocumentDetailsSheetProps = {
  filePath: string | null;
  isOpen: boolean;
  onClose: () => void;
};

export function DocumentDetailsSheet({ filePath, isOpen, onClose }: DocumentDetailsSheetProps) {
  const [contentReady, setContentReady] = useState(false);

  // Defer heavy content until after the sheet animation completes (~200ms)
  useEffect(() => {
    if (!isOpen) {
      const timer = setTimeout(() => setContentReady(false), 0);
      return () => clearTimeout(timer);
    }
    const timer = setTimeout(() => setContentReady(true), 220);
    return () => clearTimeout(timer);
  }, [isOpen]);

  const { data: previewUrl, isLoading: isLoadingPreview } = useFilePreviewUrlQuery(
    filePath || '',
    !!filePath && isOpen
  );

  if (!filePath) return null;

  const fileName = getFileName(filePath);
  const fileExtension = getFileExtension(filePath);
  const fileType = fileExtension || 'FILE';

  return (
    <Sheet open={isOpen} onOpenChange={onClose}>
      <SheetContent className="min-w-[600px] sm:max-w-[600px] flex flex-col">
        {/* Header */}
        <SheetHeader className="border-b pb-4 pr-10">
          <div className="flex items-start">
            <div className="flex-1">
              <SheetTitle className="text-left text-base font-semibold text-foreground">
                {fileName}
              </SheetTitle>
              <p className="text-sm text-muted-foreground mt-1">{fileType} Document</p>
            </div>
          </div>
        </SheetHeader>

        {/* Main Content Area */}
        <div className="flex-1 flex flex-col min-h-0 py-4">
          <DocumentPreviewContent
            previewUrl={previewUrl}
            isLoading={isLoadingPreview}
            contentReady={contentReady}
            fileExtension={fileExtension}
            fileName={fileName}
          />
        </div>

        {/* Metadata Footer — isolated so copy state doesn't re-render the preview */}
        <DocumentMetadataFooter filePath={filePath} fileType={fileType} />
      </SheetContent>
    </Sheet>
  );
}
